/**
 * hotel-variant-depth-size.js
 *
 * Controller for the PDP Depth + Size pill picker (hotel-product-variant-picker.liquid).
 *
 * The size pills are the REAL Dawn <variant-selects> radio inputs, so variant
 * resolution stays 100% native (global.js reads `fieldset input:checked`). This
 * element only:
 *   - reads the SKU-derived data-depth / data-size that Liquid stamped on each
 *     pill (it NEVER parses the human-editable size name),
 *   - filters which pills are visible for the chosen Base depth,
 *   - re-points the checked radio when depth changes, and
 *   - mirrors the full selected name into the readout.
 *
 * Asymmetric matrix note: Standard has a "Single" that Deepbase lacks. Switching
 * to a depth that has no matching size falls back to the NEAREST size by order,
 * so a valid variant is always selected (ATC never breaks) and the readout shows
 * the truthful new name.
 */
class HotelDepthSize extends HTMLElement {
  connectedCallback() {
    // The size word lives in the box's top pullout, outside this element.
    this.setSizeEl = (this.closest('.hotel-selector-box, product-info') || document).querySelector(
      '[data-hotel-set-size]'
    );
    this.depthButtons = Array.from(this.querySelectorAll('[data-depth-btn]'));

    this.depthButtons.forEach((btn) =>
      btn.addEventListener('click', () => this.setDepth(btn.dataset.depthBtn))
    );

    // Keep the pullout size word in sync with any native radio change (incl. our own).
    this.addEventListener('change', () => this.updateSetLine());

    // Initialise active depth from whatever Dawn pre-selected.
    const checked = this.checkedInput();
    const initialDepth = checked
      ? checked.dataset.depth
      : this.depthButtons[0] && this.depthButtons[0].dataset.depthBtn;
    this.applyDepth(initialDepth || 'std');
    this.updateSetLine();
  }

  checkedInput() {
    return this.querySelector('input[type="radio"]:checked');
  }

  input(depth, size) {
    return this.querySelector(
      `input[type="radio"][data-depth="${depth}"][data-size="${size}"]`
    );
  }

  applyDepth(depth) {
    this.dataset.activeDepth = depth;
    this.depthButtons.forEach((btn) => {
      const on = btn.dataset.depthBtn === depth;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  setDepth(depth) {
    if (!depth || depth === this.dataset.activeDepth) return;

    const checked = this.checkedInput();

    // Forced choice: no size picked yet → just filter the visible pills by depth and keep
    // nothing selected (auto-selecting here would defeat the "must choose a size" gate).
    if (!checked) {
      this.applyDepth(depth);
      return;
    }

    const size = checked.dataset.size;
    const order = parseInt(checked.dataset.order, 10);

    this.applyDepth(depth);

    // Carry the same size across if it exists in the new depth...
    let target = size ? this.input(depth, size) : null;

    // ...otherwise pick the nearest available size by display order.
    if (!target) {
      const candidates = Array.from(
        this.querySelectorAll(`input[type="radio"][data-depth="${depth}"]`)
      );
      candidates.sort(
        (a, b) =>
          Math.abs(parseInt(a.dataset.order, 10) - order) -
          Math.abs(parseInt(b.dataset.order, 10) - order)
      );
      target = candidates[0];
    }

    if (target && !target.checked) {
      target.checked = true;
      // Bubbles to <variant-selects> → Dawn resolves the variant natively.
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.updateSetLine();
  }

  // Mirror the selection into the set line: the depth+size span (size products) and/or
  // every colour span (colour products), e.g. "DEEPBASE SUPER KING" or "WHITE".
  updateSetLine() {
    const checked = this.checkedInput();
    if (!checked) return;
    const box = this.closest('.hotel-selector-box, product-info') || document;
    if (this.setSizeEl && checked.dataset.sizeFull) {
      const depth = checked.dataset.depthFull ? checked.dataset.depthFull + ' ' : '';
      this.setSizeEl.textContent = (depth + checked.dataset.sizeFull).toUpperCase();
    }
    if (checked.dataset.colour) {
      box.querySelectorAll('[data-hotel-set-colour]').forEach((el) => {
        el.textContent = checked.dataset.colour;
      });
    }
    // Split sets contain 2 fitted sheets: swap "1x FITTED" -> "2x FITTEDS" in the contents
    // (the only set-line change for splits); restore the base contents otherwise.
    const contentsEl = box.querySelector('[data-hotel-set-contents]');
    if (contentsEl) {
      const base = contentsEl.dataset.base != null ? contentsEl.dataset.base : contentsEl.textContent;
      contentsEl.textContent =
        checked.dataset.split === 'true' ? base.replace(/\d+x FITTED(S)?/i, '2x FITTEDS') : base;
    }
  }
}

if (!customElements.get('hotel-depth-size')) {
  customElements.define('hotel-depth-size', HotelDepthSize);
}
